import { action, runInAction, observable, makeObservable, computed } from "mobx";
import { API } from "General/API";
import { handleStoreState, CoreStore } from "Stores/RESTStore/CoreStore";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { DataNode, EventDataNode } from "antd/lib/tree";
import { stringSort } from "utils/defaultSort";
import { contactsStore } from "Stores/ContactsStore";
import { groupListStore } from "Pages/Project/Groups/Stores/GroupsStore";
import { NavigateFunction } from "react-router-dom";
import { metadataStore } from "Pages/Admin/Stores/MetadataStore";

export interface ExtendedDataNode extends DataNode {
  /**
   * ```
   * different layer types of the tree
   * MAIN: Layer 1
   * MAINDATA: Layer 2
   * LINK: Layer 3, 5, 7, 9, ...
   * SUB: Layer 4, 6, 8, 10, ...
   * ```
   */
  dataType: "MAIN" | "MAINDATA" | "LINK" | "SUB";
  children?: ExtendedDataNode[];
  valueTableId?: number;
  dataKey?: number;
  link: string;
  attachmentCount?: number;
}
export interface ObjectAndDataRow {
  key: number;
}

export class ObjectsAndDataStore extends CoreStore {
  data: API_ValueTable[] = [];
  singleData: API_ValueTable = null;
  searchResult: API_ValueTableSearchResult = null;
  objectData: API_ValueTable = null;
  currentAttributes: API_ValueTableAttribute[] = [];
  lastSearchValue: string = "";
  changedValues = null;
  currentRootId: number = null;
  treeData: ExtendedDataNode[] = [];
  treeDataMap: { [key: string]: ExtendedDataNode } = {};
  currentDataKey: number = null;
  initState: number = 0;
  treeDataEventMap: { [key: string]: EventDataNode<ExtendedDataNode> } = {};
  sideBarIsOpen: boolean = false;

  constructor() {
    super();

    makeObservable(this, {
      data: observable,
      singleData: observable,
      searchResult: observable,
      objectData: observable,
      currentAttributes: observable,
      currentRootId: observable,
      clear: action.bound,
      fetch: action.bound,
      createNewAttributes: action.bound,
      removeAttributes: action.bound,
      submitSearch: action.bound,
      fetchObjectData: action.bound,
      getAllAttributes: action.bound,
      changedValues: observable,
      setChangedValues: action.bound,
      hasChangedValues: computed,
      sideBarIsOpen: observable,
      initState: observable,
      init: action.bound,
    });
  }

  url() {
    return API_URL.VALUETABLES.getAllMainTablesOfProject(projectsDataStore.projectId);
  }

  clear() {
    this.data = [];
    this.singleData = undefined;
    this.searchResult = undefined;
    this.objectData = undefined;
    this.changedValues = undefined;
    this.currentAttributes = [];
    this.treeData = undefined;
    this.treeDataMap = {};
    this.initState = 0;
    this.sideBarIsOpen = false;
  }

  async init(navigate: NavigateFunction, pathname: string) {
    if (this.initState > 0) {
      if (this.initState === 2) {
        this.initialNavigate(pathname, navigate);
      }
      return;
    }
    this.initState = 1;
    try {
      await this.fetch();
      if (projectsDataStore.currentProject.functions.includes("METADATA")) {
        metadataStore.setMode("SETTINGS");
        await metadataStore.fetch(projectsDataStore.currentProject.id);
      }
    } finally {
      runInAction(() => {
        this.initState = 2;
      });
    }
    this.initialNavigate(pathname, navigate);
  }

  private initialNavigate(pathname: string, navigate: NavigateFunction) {
    if (objectsAndDataStore.data.length > 0 && pathname.endsWith("/values")) {
      navigate(`${objectsAndDataStore.data[0].id}`);
    }
  }

  @handleStoreState()
  async fetch(id?: number) {
    try {
      if (id) {
        this.currentRootId = id;

        const response = await API.getData<API_ValueTable>(
          API_URL.VALUETABLES.getValueTable(projectsDataStore.projectId, id)
        );
        runInAction(() => {
          this.currentRootId = id;
          this.singleData = response;
        });
      } else {
        const response = await API.getData<API_ValueTable[]>(this.url());
        runInAction(() => {
          this.data = response;

          this.treeData = this.data
            .sort((a, b) => stringSort(a, b, "name"))
            .map((table) => {
              return this.getMainTreeEntry(table);
            });
        });
      }
    } catch (error) {
      throw error;
    }
  }
  toggleSidebar() {
    this.sideBarIsOpen = !this.sideBarIsOpen;
  }

  @handleStoreState()
  async createNewAttributes(
    tableId: number | string,
    referenceId: number,
    values,
    isSubObject?: boolean,
    currentTableId?: number,
    fromSearch?: boolean
  ) {
    try {
      this.convertDateStrings(values);
      const formBody = referenceId
        ? { referenceId: referenceId, sourceId: tableId, attributesAndValues: { ...values } }
        : { attributesAndValues: { ...values } };
      const responce = await API.newData(
        API_URL.VALUETABLES.createRecord(projectsDataStore.projectId, tableId),
        formBody
      );
      responce.attributesAndValues = this.translateFromAttributeIdsToNames(
        values,
        referenceId ? this.currentAttributes : this.singleData.attributes
      );
      responce.attributesAndValues[0].key = responce.idValueTableItem;
      runInAction(async () => {
        await this.updateTreeDataAfterNew(+tableId, referenceId, responce);

        if (fromSearch) {
          await this.submitSearch(+tableId, this.lastSearchValue);
        } else if (isSubObject) {
          await this.fetchObjectData(currentTableId, referenceId);
        } else {
          await this.fetch(+tableId);
        }
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async removeAttributes(dataSource, referenceId: string) {
    try {
      const tableId = dataSource.valueTableId ? dataSource.valueTableId : dataSource.id;
      await API.deleteData(API_URL.VALUETABLES.deleteRecord(projectsDataStore.projectId, tableId, referenceId));

      runInAction(() => {
        if (!dataSource.searchResultColumns) {
          dataSource.entries = dataSource.entries.filter((val) => val.key !== referenceId);
          dataSource.entryCount--;
        } else {
          this.searchResult.searchResultColumns = (this.searchResult.searchResultColumns as [{ key: string }]).filter(
            (val) => val.key !== referenceId
          );
          dataSource.foundItemsCount--;
        }
        this.updateTreeDataAfterRemove(dataSource, +tableId, +referenceId);
      });
    } catch (error) {
      throw error;
    }
  }

  translateFromAttributeIdsToNames(values, source?) {
    const result = {};
    source = source || this.objectData.attributes;
    for (const attributeId in values) {
      const val = values[attributeId];
      const attributeName = source.find((att) => att.id === +attributeId).name;
      result[attributeName] = val;
    }
    return [result];
  }

  convertDateStrings(values) {
    for (const attributeId in values) {
      if (values[attributeId]?.format) {
        values[attributeId] = values[attributeId]?.format("DD.MM.YYYY");
      }
    }
  }

  @handleStoreState()
  async saveAttributes(tableId: number, referenceId: number, values) {
    try {
      this.convertDateStrings(values);
      const formBody = { attributesAndValues: { ...values } };
      await API.editData(API_URL.VALUETABLES.updateRecord(projectsDataStore.projectId, tableId, referenceId), formBody);

      runInAction(() => {
        this.objectData.entries = this.translateFromAttributeIdsToNames(values);
        this.changedValues = undefined;
        this.updateTreeDataAfterChanged(this.objectData, referenceId);
      });
    } catch (error) {
      throw error;
    }
  }
  get hasChangedValues() {
    return this.changedValues && Object.keys(this.changedValues)?.length > 0;
  }

  setChangedValues(values) {
    this.changedValues = values;
  }

  @handleStoreState()
  async getAllAttributes(tableId: number | string) {
    try {
      const response = await API.getData(API_URL.VALUETABLES.readAttributes(projectsDataStore.projectId, tableId));
      runInAction(() => {
        this.currentAttributes = response;
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async createAttachment(tableId: number | string, valuetableItemId: number, files: API_FileAttachment[]) {
    try {
      await API.newData(
        API_URL.VALUETABLES.createAttachment(projectsDataStore.projectId, tableId, valuetableItemId),
        files.map((f) => f.id)
      );
      runInAction(() => {
        this.updateTreeDataAfterAttachmentsAdded(+tableId, valuetableItemId, files.length);
      });
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async removeAttachment(tableId: number | string, valuetableItemId: number, files: API_FileAttachment[]) {
    try {
      const response = await API.newData(
        API_URL.VALUETABLES.removeAttachment(projectsDataStore.projectId, tableId, valuetableItemId),
        files
      );

      runInAction(() => {
        this.updateTreeDataAfterAttachmentsRemoved(+tableId, valuetableItemId, files.length);
        this.currentAttributes = response;
      });
    } catch (error) {
      throw error;
    }
  }

  getValueByDataType(value: string, dataType) {
    switch (dataType) {
      case "SYSUSER":
        return contactsStore.getUserNameById(value);
      case "SYSGROUP":
        return groupListStore.getItem(+value)?.name;
    }
    return value;
  }

  @handleStoreState()
  async submitSearch(valueTableId: number, value: string) {
    try {
      this.lastSearchValue = value;
      const formData = new FormData();
      formData.append("searchTerm", value);
      const response = await API.newData(
        API_URL.VALUETABLES.searchInValueTablesJSON(projectsDataStore.projectId, valueTableId),
        formData
      );
      runInAction(() => {
        for (const result of response.searchResultColumns) {
          for (const attributeName in result) {
            if (attributeName !== "key") {
              const value = result[attributeName];
              const attribute = this.singleData.attributes.find((a) => a.name === attributeName);
              result[attributeName] = this.getValueByDataType(value, attribute.datatype);
            }
          }
        }
        this.searchResult = response;
      });
    } catch (error) {
      throw error;
    }
  }

  parseAttributeFormat(response) {
    for (const table of response.referencedTables) {
      for (const result of table.data.entries) {
        for (const attributeName in result) {
          if (attributeName !== "key") {
            const value = result[attributeName];
            const attribute = table.data.attributes.find((a) => a.name === attributeName);
            result[attributeName] = this.getValueByDataType(value, attribute.datatype);
          }
        }
      }
    }
  }

  @handleStoreState()
  async fetchObjectData(tableId: number, dataKey: number) {
    try {
      const response = await API.getData(
        API_URL.VALUETABLES.getDetailsOfObjectInstanceJSON(projectsDataStore.projectId, tableId, dataKey)
      );
      runInAction(() => {
        this.parseAttributeFormat(response);
        this.objectData = response;
        let path = response.path;
        while (path.parent) {
          path = path.parent;
        }
        this.currentRootId = path?.valueTableId;
        this.currentDataKey = dataKey;
      });
    } catch (error) {
      throw error;
    }
  }

  getShortName(data, pathAttributes: API_ValueTableAttribute[]): string {
    let ret = "";
    pathAttributes.forEach((attribute) => (ret += data[attribute.name] + " "));
    return ret;
  }

  getValueTable(valueTableId: number): API_ValueTable {
    return this.data.find((t) => t.id === valueTableId);
  }

  getPathAttributesByValueTable(valueTable: API_ValueTable, onlyLast?: boolean): API_ValueTableAttribute[] {
    return this.getPathAttributes(valueTable.attributes, onlyLast);
  }

  getPathAttributes(attributes: API_ValueTableAttribute[], onlyLast?: boolean): API_ValueTableAttribute[] {
    let pathAttributes = attributes.filter((a) => a.showInPath);
    if (pathAttributes.length === 0) {
      pathAttributes = [attributes[0]];
    }
    return onlyLast ? [pathAttributes[pathAttributes.length - 1]] : pathAttributes;
  }

  /**
   * if a main object type (layer 1) was opened in the tree,
   * the * search will be used to load the instances of the corresponding main object.
   * Then the treedata will be updated.
   */
  @handleStoreState()
  async fetchMainObjects(node: ExtendedDataNode) {
    const formData = new FormData();
    formData.append("searchTerm", "*");

    const response = await API.newData(
      API_URL.VALUETABLES.searchInValueTablesJSON(projectsDataStore.projectId, node.valueTableId),
      formData
    );
    const table = this.getValueTable(node.valueTableId);
    const pathAttributes = this.getPathAttributesByValueTable(table);
    const isLeaf = table.referencedTables.length === 0;

    if (table.subTreeGrouping) {
      runInAction(() => {
        this.treeDataMap[node.key as string].children = this.parseSubTree(
          response.searchResultColumns,
          pathAttributes,
          node,
          response.attachmentMap
        );
      });
    } else {
      const children: ExtendedDataNode[] = response.searchResultColumns
        .map((row) => this.getMainTreeDataEntry(row, pathAttributes, node, isLeaf, response.attachmentMap[row.key]))
        .sort((a, b) => stringSort(a, b, "title"));
      this.treeDataMap[node.key as string].children = children;
    }
  }

  parseSubTree(
    data: ObjectAndDataRow[],
    pathAttributes: API_ValueTableAttribute[],
    parent: ExtendedDataNode,
    attachmentMap: { [key: string]: number }
  ) {
    if (pathAttributes.length === 1) {
      const table = this.getValueTable(parent.valueTableId);
      const children = data
        .map((row) =>
          this.getMainTreeDataEntry(
            row,
            pathAttributes,
            parent,
            table.referencedTables?.length === 0,
            attachmentMap[row.key]
          )
        )
        .sort((a, b) => stringSort(a, b, "title"));
      this.treeDataMap[parent.key as string].children = children;
      return children;
    }
    const keyAttribute = pathAttributes.shift();
    const newAddedKeys = {};
    for (const item of data) {
      const itemKey: string = parent.key + "_" + item[keyAttribute.name];
      if (!parent.children) parent.children = [];
      if (!parent.children.find((c) => c.key === itemKey)) {
        newAddedKeys[itemKey] = true;
        parent.children.push({
          key: itemKey,
          title: item[keyAttribute.name],
          children: [],
          dataType: "MAIN",
          link: `/project/${projectsDataStore.projectId}/values/${parent.valueTableId}/search-results?${
            item[keyAttribute.name]
          }`,
          isLeaf: false,
          valueTableId: parent.valueTableId,
        });
        this.treeDataMap[itemKey] = parent.children[parent.children.length - 1];
      }
    }
    parent.children.sort((a, b) => stringSort(a, b, "title"));
    for (const child of parent.children) {
      if (!newAddedKeys[child.key as string]) {
        continue;
      }
      this.parseSubTree(
        data.filter((d) => d[keyAttribute.name] === child.title),
        [...pathAttributes],
        child,
        attachmentMap
      );
    }
    return parent.children;
  }
  /**
   * if a instance of a main or sub object(layer 2, 4, 6, 8, ...) was opened in the tree,
   * the getDetailsOfObjectInstanceJSON endpoint will be used to load the instances of the corresponding parent object.
   * Then the treedata will be updated.
   */
  @handleStoreState()
  async fetchSubObjects(node: ExtendedDataNode) {
    try {
      const response: API_ValueTable = await API.getData(
        API_URL.VALUETABLES.getDetailsOfObjectInstanceJSON(projectsDataStore.projectId, node.valueTableId, node.dataKey)
      );
      runInAction(() => {
        this.parseAttributeFormat(response);
        const children: ExtendedDataNode[] = response.referencedTables
          .map((table) => this.getSubTreeEntry(table, node))
          .sort((a, b) => stringSort(a, b, "title"));

        this.treeDataMap[node.key as string].children = children;
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * get updated tree data for the antd tree after a new main or sub object instance was added
   */
  async updateTreeDataAfterNew(tableId: number, parentReferenceId: number, responce) {
    const table = this.getValueTable(tableId);

    if (!parentReferenceId) {
      // Main objects
      const node = this.treeDataMap["M_" + tableId];
      if (!node) {
        return;
      }
      if (table.subTreeGrouping) {
        this.treeDataMap["M_" + tableId].children = undefined;
        await this.fetchMainObjects(node);
        return;
      }

      if (!node.children || !node.children[0]) {
        node.children = [];
        node.isLeaf = false;
      }
      node.children.push(
        this.getMainTreeDataEntry(
          responce.attributesAndValues[0],
          this.getPathAttributes(this.singleData.attributes, table.subTreeGrouping),
          node,
          this.singleData.referencedTableCount === 0,
          0
        )
      );
    } else {
      // Sub objects
      const node = this.treeDataMap["SD_" + tableId + "_" + parentReferenceId];
      if (!node) {
        return;
      }
      if (!node.children || !node.children[0]) {
        node.children = [];
        node.isLeaf = false;
      }
      node.children.push(
        this.getSubTreeDataEntry(
          responce.attributesAndValues[0],
          this.objectData.referencedTables.find((t) => t.idTarget === tableId),
          0
        )
      );
    }
  }

  /**
   * get updated tree data for the antd tree after a main or sub object instance was removed
   */
  updateTreeDataAfterRemove(dataSource, tableId: number, parentReferenceId: number) {
    // main objects
    let key1 = "M_" + tableId;
    let key2 = "MR_" + parentReferenceId;
    if (!dataSource.valueTableId) {
      // sub objects
      key1 = "SD_" + tableId + "_" + this.currentDataKey;
      key2 = "MRS_" + tableId + "_" + parentReferenceId;
    }

    try {
      this.treeDataMap[key1].children = this.treeDataMap[key1].children.filter((c) => c.key !== key2);
      this.treeDataMap[key1].isLeaf = this.treeDataMap[key1].children.length === 0;
      delete this.treeDataMap[key2];
    } catch (e) {
      // eslint-disable-next-line no-empty
      // when the LHM isn't opened
    }
  }

  /**
   * get updated tree data for the antd tree after a main or sub object instance was changed
   */
  updateTreeDataAfterChanged(updatedValueTable: API_ValueTable, referenceId: number) {
    try {
      const item =
        this.treeDataMap["MR_" + referenceId] || this.treeDataMap["MRS_" + updatedValueTable.id + "_" + referenceId];
      const pathAttributes = this.getPathAttributesByValueTable(updatedValueTable, updatedValueTable.subTreeGrouping);
      item.title = this.getShortName(updatedValueTable.entries[0], pathAttributes);
    } catch (e) {
      // eslint-disable-next-line no-empty
      // when the LHM isn't opened
    }
  }

  updateTreeDataAfterAttachmentsAdded(tableId: number, referenceId: number, attachmentCount: number) {
    try {
      const item = this.treeDataMap["MR_" + referenceId] || this.treeDataMap["MRS_" + tableId + "_" + referenceId];
      if (!item.attachmentCount) {
        item.attachmentCount = 0;
      }
      item.attachmentCount += attachmentCount;
    } catch (e) {
      // eslint-disable-next-line no-empty
      // when the LHM isn't opened
    }
  }

  updateTreeDataAfterAttachmentsRemoved(tableId: number, referenceId: number, attachmentCount: number) {
    try {
      const item = this.treeDataMap["MR_" + referenceId] || this.treeDataMap["MRS_" + tableId + "_" + referenceId];
      item.attachmentCount -= attachmentCount;
    } catch (e) {
      // eslint-disable-next-line no-empty
      // when the LHM isn't opened
    }
  }

  /**
   * get a item of layer 1 of the tree
   */
  private getMainTreeEntry(table: API_ValueTable): ExtendedDataNode {
    const elem: ExtendedDataNode = {
      title: table.name,
      key: "M_" + table.id,
      link: `/project/${projectsDataStore.projectId}/values/${table.id}/search-results?*`,
      dataType: "MAIN",
      valueTableId: table.id,
      isLeaf: table.entryCount === 0,
    };
    this.treeDataMap[elem.key as string] = elem;
    return elem;
  }

  /**
   * get a item of layer 2 of the tree
   */
  private getMainTreeDataEntry(
    row,
    pathAttributes: API_ValueTableAttribute[],
    node: ExtendedDataNode,
    isLeaf: boolean,
    attachmentCount: number
  ): ExtendedDataNode {
    const elem: ExtendedDataNode = {
      title: this.getShortName(row, pathAttributes),
      key: "MR_" + row.key,
      dataType: "MAINDATA",
      link: `/project/${projectsDataStore.projectId}/values/${node.valueTableId}/sub-object/${row.key}`,
      valueTableId: node.valueTableId,
      dataKey: row.key,
      isLeaf: isLeaf,
      attachmentCount,
    };
    this.treeDataMap[elem.key as string] = elem;
    return elem;
  }

  /**
   * get a item of layer 3 (or 5, 7, 9, ....) of the tree
   */
  private getSubTreeEntry(table: API_ValueTableReference, node: ExtendedDataNode): ExtendedDataNode {
    if (table.data.subTreeGrouping) {
      const children = this.parseSubTree(
        table.data.entries as ObjectAndDataRow[],
        this.getPathAttributesByValueTable(table.data),
        node,
        table.data.attachmentMap
      );
      const elem: ExtendedDataNode = {
        title: table.data.name,
        key: "SD_" + table.idTarget + "_" + node.dataKey,
        dataType: "LINK",
        valueTableId: node.valueTableId,
        link: `/project/${projectsDataStore.projectId}/values/${node.valueTableId}/sub-object/${node.dataKey}/${table.data.name}`,
        isLeaf: table.data.entryCount === 0,
        dataKey: node.dataKey,
        children: children,
      };
      this.treeDataMap[elem.key as string] = elem;
      return elem;
    } else {
      const children = (table.data.entries as [{ key: number }])
        .map((row) => this.getSubTreeDataEntry(row, table, table.data.attachmentMap[row.key]))
        .sort((a, b) => stringSort(a, b, "title"));

      const elem: ExtendedDataNode = {
        title: table.data.name,
        key: "SD_" + table.idTarget + "_" + node.dataKey,
        dataType: "LINK",
        valueTableId: node.valueTableId,
        link: `/project/${projectsDataStore.projectId}/values/${node.valueTableId}/sub-object/${node.dataKey}/${table.data.name}`,
        isLeaf: table.data.entryCount === 0,
        dataKey: node.dataKey,
        children: children,
      };
      this.treeDataMap[elem.key as string] = elem;
      return elem;
    }
  }

  /**
   * get a item of layer 4 (or 6, 8, 10, ...) of the tree
   */
  private getSubTreeDataEntry(
    row: { key: number },
    table: API_ValueTableReference,
    attachmentCount: number
  ): ExtendedDataNode {
    const elem: ExtendedDataNode = {
      title: this.getShortName(row, this.getPathAttributesByValueTable(table.data, table.data.subTreeGrouping)),
      key: "MRS_" + table.idTarget + "_" + row.key,
      dataType: "SUB",
      valueTableId: table.idTarget,
      link: `/project/${projectsDataStore.projectId}/values/${table.data.id}/sub-object/${row.key}`,
      dataKey: row.key,
      isLeaf: table.data.referencedTableCount === 0,
      attachmentCount,
    };
    this.treeDataMap[elem.key as string] = elem;
    return elem;
  }
}

export const objectsAndDataStore = new ObjectsAndDataStore();
